<template>
  <div class="flowchart-preview">
    <svg class="flowchart-canvas" :viewBox="`0 0 ${canvasWidth} ${canvasHeight}`" role="img" :aria-label="`${data.title || '流程图'}预览`">
      <defs>
        <marker id="flowchart-arrow" markerWidth="10" markerHeight="10" refX="8" refY="3" orient="auto" markerUnits="strokeWidth">
          <path d="M0,0 L0,6 L9,3 z" fill="#94a69f"></path>
        </marker>
        <filter id="flowchart-shadow" x="-20%" y="-30%" width="140%" height="160%">
          <feDropShadow dx="0" dy="3" stdDeviation="4" flood-color="#243f35" flood-opacity=".1"></feDropShadow>
        </filter>
      </defs>

      <g class="flowchart-edges">
        <g v-for="edge in normalizedEdges" :key="`${edge.from}-${edge.to}-${edge.label || ''}`">
          <path :d="edgePath(edge)" marker-end="url(#flowchart-arrow)"></path>
          <text v-if="edge.label" :x="edgeLabelPoint(edge).x" :y="edgeLabelPoint(edge).y">{{ edge.label }}</text>
        </g>
      </g>

      <g
        v-for="node in normalizedNodes"
        :key="node.id"
        class="flowchart-node"
        :class="`node-${node.type}`"
        :transform="`translate(${node.x}, ${node.y})`"
      >
        <polygon v-if="node.type === 'decision'" :points="decisionPoints(node)"></polygon>
        <rect
          v-else
          :x="-node.width / 2"
          :y="-node.height / 2"
          :width="node.width"
          :height="node.height"
          :rx="node.type === 'start' || node.type === 'end' ? node.height / 2 : 12"
        ></rect>
        <text text-anchor="middle">
          <tspan
            v-for="(line, index) in nodeLines(node)"
            :key="`${node.id}-${index}`"
            x="0"
            :dy="index === 0 ? `${-(nodeLines(node).length - 1) * 9}px` : '18px'"
          >{{ line }}</tspan>
        </text>
      </g>
    </svg>
    <p class="flowchart-hint">根据提取出的节点与连接关系生成流程结构预览</p>
  </div>
</template>

<script>
export default {
  name: 'FlowchartPreview',
  props: {
    data: {
      type: Object,
      required: true
    }
  },
  computed: {
    normalizedNodes () {
      const source = Array.isArray(this.data.nodes) && this.data.nodes.length
        ? this.data.nodes
        : this.nodesFromOfficeShapes()
      return source.slice(0, 30).map((node, index) => ({
        id: String((node && node.id) || `node-${index + 1}`),
        label: String((node && (node.label || node.title || node.text)) || `步骤 ${index + 1}`),
        type: this.normalizeType(node && (node.type || node.geometry), index, source.length),
        x: Number(node && node.x) || 120 + (index % 4) * 220,
        y: Number(node && node.y) || 125 + Math.floor(index / 4) * 150,
        width: Math.min(190, Math.max(130, Number(node && node.width) || 160)),
        height: Math.min(110, Math.max(58, Number(node && node.height) || 68))
      }))
    },
    normalizedEdges () {
      if (Array.isArray(this.data.edges) && this.data.edges.length) {
        return this.data.edges.map(edge => ({
          from: String(edge.from || edge.source || ''),
          to: String(edge.to || edge.target || ''),
          label: String(edge.label || '')
        })).filter(edge => edge.from && edge.to)
      }
      return this.normalizedNodes.slice(0, -1).map((node, index) => ({
        from: node.id,
        to: this.normalizedNodes[index + 1].id,
        label: ''
      }))
    },
    canvasWidth () {
      return Math.max(660, ...this.normalizedNodes.map(node => node.x + node.width / 2 + 60))
    },
    canvasHeight () {
      return Math.max(620, ...this.normalizedNodes.map(node => node.y + node.height / 2 + 60))
    }
  },
  methods: {
    nodesFromOfficeShapes () {
      const shapes = Array.isArray(this.data.shapes) ? this.data.shapes : []
      const labels = Array.isArray(this.data.labels) ? this.data.labels : []
      const length = Math.max(shapes.length, labels.length)
      return Array.from({ length }, (_, index) => ({
        id: `office-node-${index + 1}`,
        label: labels[index] || `步骤 ${index + 1}`,
        geometry: shapes[index] && shapes[index].geometry
      }))
    },
    normalizeType (value, index, length) {
      const type = String(value || '').toLowerCase()
      if (type.includes('decision')) return 'decision'
      if (type.includes('start') || type.includes('terminator')) return index === length - 1 ? 'end' : 'start'
      if (type === 'end') return 'end'
      if (type === 'start') return 'start'
      return 'process'
    },
    nodeById (id) {
      return this.normalizedNodes.find(node => node.id === id)
    },
    edgePath (edge) {
      const source = this.nodeById(edge.from)
      const target = this.nodeById(edge.to)
      if (!source || !target) return ''
      const horizontalDistance = Math.abs(target.x - source.x)
      const verticalDistance = Math.abs(target.y - source.y)
      if (verticalDistance >= horizontalDistance || target.x < source.x) {
        const startY = source.y + source.height / 2
        const endY = target.y - target.height / 2 - 7
        const middleY = startY + (endY - startY) / 2
        return `M ${source.x} ${startY} V ${middleY} H ${target.x} V ${endY}`
      }
      const startX = source.x + source.width / 2
      const endX = target.x - target.width / 2 - 7
      const middleX = startX + (endX - startX) / 2
      return `M ${startX} ${source.y} H ${middleX} V ${target.y} H ${endX}`
    },
    edgeLabelPoint (edge) {
      const source = this.nodeById(edge.from)
      const target = this.nodeById(edge.to)
      if (!source || !target) return { x: 0, y: 0 }
      const horizontalDistance = Math.abs(target.x - source.x)
      const verticalDistance = Math.abs(target.y - source.y)
      if (verticalDistance >= horizontalDistance || target.x < source.x) {
        return {
          x: source.x + (target.x - source.x) / 2 + 12,
          y: source.y + source.height / 2 + (target.y - target.height / 2 - source.y - source.height / 2) / 2 - 8
        }
      }
      return {
        x: source.x + source.width / 2 + 18,
        y: source.y === target.y ? source.y - 10 : source.y + (target.y - source.y) / 2 - 8
      }
    },
    decisionPoints (node) {
      const halfWidth = node.width / 2
      const halfHeight = node.height / 2
      return `0,${-halfHeight} ${halfWidth},0 0,${halfHeight} ${-halfWidth},0`
    },
    nodeLines (node) {
      if (node.label.includes('\n')) return node.label.split('\n').slice(0, 3)
      if (node.label.length <= 12) return [node.label]
      return [node.label.slice(0, 12), node.label.slice(12, 24)]
    }
  }
}
</script>

<style scoped>
.flowchart-preview {
  position: relative;
  margin-top: 13px;
  overflow: hidden;
  border: 1px solid #e4ebe8;
  border-radius: 8px;
  background: linear-gradient(145deg, #fbfffd 0%, #f4f8fb 100%);
}

.flowchart-canvas {
  display: block;
  width: 100%;
  min-height: 400px;
}

.flowchart-edges path {
  fill: none;
  stroke: #94a69f;
  stroke-width: 2;
}

.flowchart-edges text {
  fill: #71817b;
  font-size: 13px;
  font-family: "Microsoft YaHei", "PingFang SC", sans-serif;
}

.flowchart-node polygon,
.flowchart-node rect {
  fill: #fff;
  stroke: #17ae77;
  stroke-width: 2;
  filter: url(#flowchart-shadow);
}

.flowchart-node.node-decision polygon {
  fill: #fff9ed;
  stroke: #e9a24f;
}

.flowchart-node.node-start rect,
.flowchart-node.node-end rect {
  fill: #eaf9f3;
}

.flowchart-node text {
  fill: #31463f;
  font-size: 14px;
  font-weight: 600;
  font-family: "Microsoft YaHei", "PingFang SC", sans-serif;
  dominant-baseline: middle;
}

.flowchart-hint {
  margin: -3px 12px 10px;
  color: #94a19c;
  font-size: 11px;
  text-align: right;
}
</style>
